/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  X,
  Save,
  Globe,
  ChevronDown,
  Check,
  AlertTriangle
} from 'lucide-react';
import { accountService } from '../services/accountService';
import toastService from '../utils/toastService';

const EditAccount = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hasSubAccounts, setHasSubAccounts] = useState(false);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    status: 'active'
  });
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    const loadAccount = async () => {
      try {
        setLoading(true);

        // Try to use account data passed from the list view first (has status and counts)
        const initialAccount = location.state?.account;

        if (initialAccount && initialAccount.id === id) {
          setHasSubAccounts(parseInt(initialAccount.subAccountCount) > 0);
          setForm({
            name: initialAccount.name || '',
            slug: initialAccount.slug || '',
            status: initialAccount.status || 'active',
            truvideoAccountUID: initialAccount.truvideoAccountUID || ''
          });
          setLoading(false);
          return;
        }

        const { data: account } = await accountService.getAccountById(id);

        setHasSubAccounts(account.hasSubAccounts || false);
        setForm({
          name: account.name,
          slug: account.slug,
          status: account.status || 'active',
          truvideoAccountUID: account.truvideoAccountUID || ''
        });
      } catch (err) {
        toastService.error('Failed to load account details');
        navigate('/accounts');
      } finally {
        setLoading(false);
      }
    };
    loadAccount();
  }, [id, navigate, location.state]);



  const validateForm = () => {
    const errors = {};
    if (!form.name.trim()) { errors.name = 'Account name is required'; }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { return; }
    try {
      setSubmitting(true);
      await accountService.updateAccount(id, {
        name: form.name.trim(),
        status: form.status
      });
      toastService.success('Account updated successfully');
      navigate('/accounts');
    } catch (err) {
      const msg = err.response?.data?.message;
      const errorMsg = Array.isArray(msg) ? msg[0] : (msg || 'Failed to update account');
      toastService.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4">
        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
        <p className="text-base-content/40 font-black uppercase tracking-widest text-xs">Loading Account...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-auto lg:h-[calc(100vh-3rem)] lg:overflow-hidden rounded-2xl border border-base-300 shadow-sm bg-base-100">
      <div className="flex-1 flex flex-col overflow-y-auto bg-base-100/50 backdrop-blur-md">

        {/* Header Section */}
        <div className="p-6 sm:p-8 border-b border-base-300 flex-shrink-0 flex justify-between items-start">
          <div>
            <h1 className="page-title mb-1">Update Account</h1>
          </div>
          <button onClick={() => navigate('/accounts')} className="p-2 hover:bg-base-200 rounded-xl transition-colors text-base-content/40 hover:text-base-content">
            <X size={24} />
          </button>
        </div>

        {/* Sub-accounts restriction banner */}
        {hasSubAccounts && (
          <div className="mx-6 sm:mx-8 mt-6 flex items-start gap-3 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
            <AlertTriangle size={16} className="text-amber-500 mt-0.5 shrink-0" />
            <p className="text-amber-500 text-xs font-bold">
              This account has active sub-accounts. Status cannot be changed while sub-accounts exist.
            </p>
          </div>
        )}

        {/* Form Content */}
        <div className="p-6 sm:p-8 max-w-[1200px]">
          <form onSubmit={handleSubmit} className="space-y-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

              {/* Account Name — editable */}
              <div className="space-y-2">
                <label htmlFor="name" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                  Account Name *
                </label>
                <input
                  id="name"
                  type="text"
                  maxLength={50}
                  placeholder="e.g. Acme Corporation"
                  className={`w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content font-bold focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all ${fieldErrors.name ? 'border-error' : 'border-base-300'}`}
                  value={form.name}
                  onChange={e => {
                    setForm(f => ({ ...f, name: e.target.value }));
                    if (fieldErrors.name) setFieldErrors(prev => ({ ...prev, name: null }));
                  }}
                />
                {fieldErrors.name && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.name}</p>}
              </div>

              {/* Slug — read-only, locked after creation */}
              <div className="space-y-2">
                <label htmlFor="slug" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                  Slug
                </label>
                <div className="relative">
                  <Globe size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-base-content/20" />
                  <input
                    id="slug"
                    type="text"
                    readOnly
                    tabIndex={-1}
                    className="w-full pl-11 pr-4 py-3 bg-base-200/60 border border-base-300 rounded-xl text-base-content/50 font-bold outline-none cursor-not-allowed"
                    value={form.slug}
                  />
                </div>
                <p className="text-base-content/30 text-[10px] font-medium">Slug cannot be changed after account creation.</p>
              </div>

              {/* Status — editable, but locked if sub-accounts exist */}
              {/* <div className="space-y-2">
                <label className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                  Status *
                </label>
                <div className="relative" ref={statusDropdownRef}>
                  <button
                    type="button"
                    disabled={hasSubAccounts}
                    onClick={() => !hasSubAccounts && setIsStatusOpen(!isStatusOpen)}
                    className={`flex items-center justify-between gap-2 w-full rounded-xl px-4 py-3 border font-bold outline-none transition-all group ${hasSubAccounts
                      ? 'bg-base-200/60 border-base-300 text-base-content/40 cursor-not-allowed'
                      : 'bg-base-100 border-base-300 text-base-content focus:ring-2 focus:ring-primary focus:border-primary'
                      }`}
                  >
                    <span className="truncate flex-1 text-left uppercase text-[10px] tracking-wider">{form.status}</span>
                    <ChevronDown size={14} className={`text-base-content/20 transition-transform ${!hasSubAccounts ? 'group-hover:text-primary' : ''} ${isStatusOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isStatusOpen && !hasSubAccounts && (
                    <div className="absolute left-0 right-0 top-full mt-2 bg-base-100 border border-base-content/10 rounded-xl shadow-2xl z-[100] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 flex flex-col">
                      {['active', 'inactive'].map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => { setForm(f => ({ ...f, status: s })); setIsStatusOpen(false); }}
                          className={`w-full px-4 py-3 text-left hover:bg-primary/5 transition-all flex items-center gap-2 group border-b border-base-content/5 last:border-none ${form.status === s ? 'bg-primary/5' : ''}`}
                        >
                          <span className={`text-[11px] font-black uppercase tracking-widest ${form.status === s ? 'text-primary' : 'text-base-content/40 group-hover:text-primary'}`}>{s}</span>
                          {form.status === s && <Check size={12} className="text-primary ml-auto" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {hasSubAccounts && (
                  <p className="text-amber-500 text-[10px] font-bold">Remove all sub-accounts before changing this status.</p>
                )}
              </div> */}

            </div>

            {/* Actions */}
            <div className="flex gap-4 pt-12 border-t border-base-content/5">
              <button
                type="button"
                onClick={() => navigate('/accounts')}
                className="flex-1 py-4 px-8 border border-base-content/20 rounded-xl font-black uppercase tracking-[0.2em] text-xs text-base-content/40 hover:bg-base-content/5 hover:text-base-content transition-all"
              >
                Cancel
              </button>
              <button type="submit" disabled={submitting} className="flex-1 btn-premium">
                {submitting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Save size={18} />
                    <span>Save Changes</span>
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditAccount;
